import { useState } from 'react';
import { Bar, Card, Row } from '../components/Card';
import * as fmt from '../format';
import type { Store } from '../store';
import { CheckpointDialog } from './Checkpoint';
import { RecordDialog } from './Record';

export function Today({ store }: { store: Store }) {
  const [recording, setRecording] = useState(false);
  const [editingCheckpoint, setEditingCheckpoint] = useState(false);
  const { summary, pace, walk, days } = store;

  if (!summary) return <p className="muted">{store.loading ? 'Loading…' : 'No data yet.'}</p>;

  const todayRow = days.find((d) => d.date === summary.asOf);
  const todaySteps = todayRow?.steps
    ?? (walk && walk.stepsPerMile > 0 ? summary.rank.todaysMiles * walk.stepsPerMile : 0);
  const tracking = summary.targetTracking;

  return (
    <>
      <div className="toolbar">
        <button className="primary" onClick={() => setRecording(true)} disabled={!walk}>
          Record steps
        </button>
      </div>

      <div className="grid">
        <Card>
          <div className="headline">
            <p className="headline__caption">{fmt.mediumDate(summary.asOf)}</p>
            <p className="headline__value">{fmt.steps(todaySteps)}</p>
            <p className="headline__sub">{fmt.miles(summary.rank.todaysMiles)}</p>
          </div>
        </Card>

        {pace?.current && (
          <Card title="Pace">
            <p className="headline__value headline__value--small">
              {fmt.speed(pace.current.milesPerHour)}
            </p>
            <Row label="Cadence" value={fmt.cadence(pace.current.stepsPerMinute)} />
            <Row
              label="Measured over"
              value={`${fmt.duration(pace.current.minutes)} · ${pace.current.readings} readings`}
            />
            {pace.versusCalibration !== null && (
              <Row
                label="Versus calibration"
                value={`${fmt.percent(Math.abs(pace.versusCalibration - 1), 0)} ${
                  pace.versusCalibration >= 1 ? 'faster' : 'slower'
                }`}
                tone={pace.versusCalibration >= 1 ? 'good' : 'warn'}
              />
            )}
            {!pace.moving && pace.minutesSinceLastReading !== null && (
              <p className="muted">
                Last reading {fmt.duration(pace.minutesSinceLastReading)} ago.
              </p>
            )}
          </Card>
        )}

        <Card title={summary.toTodaysTarget.met ? "Today's target met" : "To today's target"}>
          <Bar fraction={summary.rank.todaysMiles / Math.max(summary.targetPerDayMiles, 0.01)} />
          {summary.toTodaysTarget.met ? (
            <Row label="Over target" value={fmt.miles(summary.toTodaysTarget.miles)} />
          ) : (
            <>
              <Row label="Remaining" value={fmt.miles(summary.toTodaysTarget.miles)} />
              <Row label="Steps needed" value={fmt.steps(summary.toTodaysTarget.steps)} />
              <Row
                label={fmt.paceLabel(summary.paceBasis)}
                value={fmt.duration(summary.toTodaysTarget.estimatedTimeMinutes)}
              />
            </>
          )}
          <Row label="Daily target" value={fmt.miles(summary.targetPerDayMiles)} />
        </Card>

        <Card title="Standing">
          <Row
            label="Today's rank"
            value={fmt.rank(summary.rank.todaysRank, summary.rank.totalRankedDays)}
          />
          {summary.streaks && (
            <Row
              label="Streak"
              value={fmt.streak(summary.streaks.current)}
            />
          )}
          {summary.rank.toNextRank && (
            <Row
              label="To next rank"
              value={`${fmt.miles(summary.rank.toNextRank.miles)} · ${fmt.duration(summary.rank.toNextRank.minutes)}`}
            />
          )}
          <Row
            label="To top 10"
            value={
              summary.rank.toTop10
                ? `${fmt.miles(summary.rank.toTop10.miles)} · ${fmt.duration(summary.rank.toTop10.minutes)}`
                : 'Yes'
            }
          />
          {summary.rank.toTopRank && (
            <Row label="To best day" value={fmt.miles(summary.rank.toTopRank.miles)} />
          )}
        </Card>

        {/* The diff curve, which is the figure that actually says whether the
            walk is going well. Where you are against the plan, the best you
            have ever been against it, and what it would take to get back
            there. On Today rather than only on Progress because it is the
            answer to "should I do another mile tonight". */}
        <Card title="Peak">
          <Row
            label={summary.currentDiffMiles >= 0 ? 'Ahead of plan' : 'Behind plan'}
            value={fmt.signedMiles(summary.currentDiffMiles)}
            tone={summary.currentDiffMiles >= 0 ? 'good' : 'warn'}
          />
          <Row
            label="Furthest ahead"
            value={
              summary.maxDistanceAheadMiles === null
                ? 'Never ahead yet'
                : fmt.miles(summary.maxDistanceAheadMiles)
            }
          />
          {summary.behindMaxDifferenceMiles > 0 ? (
            <>
              <Row label="Below your peak" value={fmt.miles(summary.behindMaxDifferenceMiles)} />
              <Row label="Time to regain" value={fmt.duration(summary.timeToMaxMinutes)} />
            </>
          ) : (
            <Row label="Position" value="At your best" tone="good" />
          )}
        </Card>
      </div>

      {/* Full width, below the grid: the checkpoint is the thing being
          chased, and a column-width card buries it. */}
      <Card
        wide
        title={tracking?.label ?? 'Checkpoint'}
        action={
          <button onClick={() => setEditingCheckpoint(true)} disabled={!walk}>
            {tracking ? 'Change' : 'Set'}
          </button>
        }
      >
        {tracking ? (
          tracking.reached ? (
            <>
              <p className="good">🏁 Passed {fmt.miles(tracking.miles)}</p>
              <p className="muted">Set the next one to keep something in front of you.</p>
            </>
          ) : (
            <>
              <Row label="Target" value={fmt.miles(tracking.miles)} />
              <Row label="Remaining" value={fmt.miles(tracking.differenceMiles)} />
              <Row label={fmt.paceLabel(summary.paceBasis)} value={fmt.duration(tracking.estimatedTimeMinutes)} />
            </>
          )
        ) : (
          <p className="muted">
            No checkpoint set. Pick a distance worth chasing — a state line, or the next round
            number.
          </p>
        )}
      </Card>

      {recording && <RecordDialog store={store} onClose={() => setRecording(false)} />}
      {editingCheckpoint && (
        <CheckpointDialog store={store} onClose={() => setEditingCheckpoint(false)} />
      )}
    </>
  );
}
